import React from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import moment from 'moment'
import '../../assets/css/pages/Expeditions/ExpeditionsAssignmentModal.css'
import '../../assets/css/sharedStyles.css'
import SmallCalendar from './SmallCalendar'
import Select from 'react-select'
import { SelfGroupCard, StudentGroupCard, StudentTag } from './GroupInteractions'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
	faPeopleGroup,
	faPlus,
	faCircleCheck,
	faCircleExclamation
} from '@fortawesome/free-solid-svg-icons'
import LoadingGif from '../../assets/img/loading-pinwheel.gif'
import _ from 'lodash'
import ExpeditionContextProvider, {
	DATE,
	ExpeditionContext,
	UNGROUPED,
	handleDeleteModeConversion,
	useExpeditionUtils
} from './Expedition_utils'
import { isPlural } from '../../util'
import { DefaultSelectJSS } from '../../assets/scss/components/form/DefaultSelectJSS'

/*
NOTE: this is the structure of handling student groups:
{
	expeditionId: expedition.id,
	expeditionVersionNumber: expedition.version,
	sectionId: assignableSections[e.target.value].id,
	groups: [
		[{studentUUID:studentFullName},{studentUUID:studentFullName}],
		[]
	],
	ungroupedStudents:[{studentUUID:studentFullName}]
}
*/
const ExpeditionsAssignmentModal = ({ expedition, isDelete, isModify, handleClose }) => {
	const [assignedExpedition, setAssignedExpedition] = React.useState(
		isDelete && handleDeleteModeConversion(expedition)
	)

	return (
		<ExpeditionContextProvider
			isDelete={isDelete}
			assignedStudentGroups={assignedExpedition.assignedStudentGroups}>
			<ExpeditionsAssignment
				expedition={isDelete ? assignedExpedition.expedition : expedition}
				isModify={isModify}
				handleClose={handleClose}
			/>
		</ExpeditionContextProvider>
	)
}

ExpeditionsAssignmentModal.propTypes = {
	expedition: PropTypes.object.isRequired,
	isDelete: PropTypes.bool,
	isModify: PropTypes.bool,
	handleClose: PropTypes.func.isRequired
}

const ExpeditionsAssignment = ({ expedition, isModify, handleClose }) => {
	const currentUser = useSelector(state => state.currentUser.data)
	const {
		isLoading,
		selectedDate,
		setSelectedDate,
		assignableSections,
		studentGroups,
		setStudentGroups,
		assignmentMessage,
		isDelete
	} = React.useContext(ExpeditionContext)
	const { handleCreateStudentGroups, handleAssignExpedition } = useExpeditionUtils()

	const onSelect = date => {
		setSelectedDate(moment(date).utc().format('MM/DD/YYYY'))
	}
	return (
		<dialog className='ExpeditionsAssignmentModal__background'>
			<div className='ExpeditionsAssignmentModal__container'>
				<section className='ExpeditionsAssignmentModal__assignment'>
					<div className='ExpeditionsAssignmentModal__assignment--detail'>
						<h3 className='ExpeditionsAssignmentModal__name'>
							{expedition.expedition_name}
						</h3>
						<p className='ExpeditionsAssignmentModal__description'>
							{expedition.expedition_description}
						</p>
					</div>
					{/*TODO: temporarily calling it small calendar, need to refactor out the original assignmentCalendar at a later point */}
					<div className='ExpeditionsAssignmentModal__assignment--datePicker'>
						{/* TODO: SmallCalendar should be moved out to be a shared component in /components/Shared path */}
						{!isDelete && (
							<SmallCalendar
								selectedDate={
									!_.isEmpty(selectedDate) && moment(selectedDate).isValid()
										? moment(selectedDate).utc().toString()
										: ''
								}
								onSelect={onSelect}
							/>
						)}

						<div className={'ExpeditionAssignmentModal__input--date SimpleInput--text'}>
							<label>Queue on date</label>
							<input
								type='text'
								value={selectedDate}
								disabled={isDelete}
								className={
									(!_.isEmpty(selectedDate) && !moment(selectedDate).isValid()) ||
									assignmentMessage?.type === DATE
										? 'error'
										: ''
								}
								onChange={e => {
									setSelectedDate(e.target.value)
								}}
							/>
						</div>
					</div>
					{isLoading && (
						<img
							className='loading'
							src={LoadingGif}
							style={{
								display: 'block',
								margin: '32px auto',
								width: '24px',
								height: '24px'
							}}
							alt='Loading'
						/>
					)}

					{!_.isEmpty(assignmentMessage) && !isLoading && (
						<div className='ExpeditionsAssignmentModal__assignment--confirmMessage'>
							<FontAwesomeIcon
								color={assignmentMessage.success ? '#4FCA15' : '#FB6969'}
								icon={
									assignmentMessage.success ? faCircleCheck : faCircleExclamation
								}
							/>
							<span>{assignmentMessage.message}</span>
							{assignmentMessage.detail && (
								<div>
									{Object.keys(assignmentMessage.detail).map((groupKey, ind) => {
										return (
											<div
												className='ExpeditionAssignmentModal__assignment--errorGroup'
												key={`errorGroup-${ind + 1}`}>
												<span>Group {parseInt(groupKey) + 1}</span>
												<span>
													{`${
														assignmentMessage.detail[groupKey]
													} student${isPlural(
														assignmentMessage.detail[groupKey]
													)}`}
												</span>
											</div>
										)
									})}
								</div>
							)}
						</div>
					)}
					<div className='ExpeditionsAssignmentModal__assignment--actions'>
						{isDelete ? (
							<DeleteActionButtons handleClose={handleClose} />
						) : (
							<SubmissionActionButtons
								handleClose={handleClose}
								handleAssignExpedition={handleAssignExpedition}
							/>
						)}
					</div>
				</section>

				<section className='ExpeditionsAssignmentModal__groups'>
					<div className='ExpeditionsAssignmentModal__groups--sections'>
						<h3>
							Groups{' '}
							{`(${
								studentGroups.isSelfAssignment ? 1 : _.size(studentGroups.groups)
							})`}
						</h3>
						<div className='ExpeditionsAssignmentModal__groups--sectionsSelection'>
							<span>from class:</span>
							{isDelete ? (
								<span>{studentGroups.sectionName}</span>
							) : (
								<Select
									className='ExpeditionsAssignmentModal__groups--sectionsSelection__select'
									onChange={e => {
										const isSelfAssigned = e.value === 'myself'
										let assignees = {
											expeditionId: expedition.id,
											expeditionVersionNumber: expedition.version,
											isSelfAssignment: isSelfAssigned
										}

										if (!isSelfAssigned) {
											var { courseId, sectionInd } = e.value
											var assignableStudents = _.shuffle(
												assignableSections[courseId][sectionInd].students
											)
											assignees = {
												...assignees,
												sectionId:
													assignableSections[courseId][sectionInd].id,
												sectionName:
													assignableSections[courseId][sectionInd].name,
												groups: handleCreateStudentGroups(
													assignableStudents
												),
												ungroupedStudents: []
											}
										}

										setStudentGroups(assignees)
									}}
									styles={DefaultSelectJSS}
									components={{
										IndicatorSeparator: () => null
									}}
									placeholder={'Selected Class'}
									options={[
										{ label: 'Myself', value: 'myself' },
										...Object.values(assignableSections).map(sections => {
											return {
												label: sections[0].course.name,
												options: sections.map((section, sectionInd) => ({
													label: section.name,
													value: {
														courseId: section.courseId,
														sectionInd: sectionInd
													}
												}))
											}
										})
									]}
								/>
							)}
						</div>

						{!isDelete && !studentGroups.isSelfAssignment && (
							<button
								className='ExpeditionAssignmentModal__groups--sectionsAddGroup'
								onClick={() => {
									setStudentGroups(prev => {
										const newStudentGroups = [...prev.groups, []]

										return { ...prev, groups: newStudentGroups }
									})
								}}
								disabled={
									_.isEmpty(studentGroups.groups) &&
									_.isEmpty(studentGroups.ungroupedStudents)
								}>
								<FontAwesomeIcon
									icon={faPlus}
									style={{ width: '20px', height: '20px' }}
								/>
							</button>
						)}
					</div>

					<div className='ExpeditionsAssignmentModal__groups--students'>
						<div className='ExpeditionsAssignmentModal__groups--students--grouped'>
							{studentGroups.isSelfAssignment && <SelfGroupCard />}
							{!studentGroups.isSelfAssignment &&
								!_.isEmpty(studentGroups.groups) &&
								studentGroups.groups.map((group, ind) => {
									return (
										<StudentGroupCard
											key={`studentGroup-${ind + 1}`}
											students={group}
											groupIndex={ind}
											maxAllowableStudent={5}
										/>
									)
								})}
						</div>
						{/* TODO: consider making this a separate component for organization */}
						{!_.isEmpty(studentGroups.ungroupedStudents) && (
							<div className='ExpeditionsAssignmentModal__groups--students--ungrouped'>
								<h4>
									<span>Ungrouped </span>
									<span>
										<FontAwesomeIcon
											icon={faPeopleGroup}
											style={{
												width: '16px',
												height: 'auto',
												marginRight: '4px'
											}}
										/>
										{studentGroups?.ungroupedStudents.length}
									</span>
								</h4>
								<div className='UngroupedStudents__body'>
									{studentGroups.ungroupedStudents.map((student, ind) => (
										<StudentTag
											key={Object.keys(student)[0]}
											view={UNGROUPED}
											student={student}
											location={{
												groupIndex: 'ungroupedStudents',
												studentIndex: ind
											}}
										/>
									))}
								</div>
							</div>
						)}
					</div>
				</section>
			</div>
		</dialog>
	)
}

ExpeditionsAssignment.propTypes = {
	expedition: PropTypes.object.isRequired,
	isDelete: PropTypes.bool,
	isModify: PropTypes.bool,
	handleClose: PropTypes.func.isRequired
}

const SubmissionActionButtons = ({ handleClose, handleAssignExpedition }) => {
	const { isSubmitted, studentGroups } = React.useContext(ExpeditionContext)
	return isSubmitted ? (
		<button
			className='ExpeditionsAssignmentModal__assignment--actions--done Button--goAhead'
			onClick={() => handleClose()}>
			Done
		</button>
	) : (
		<React.Fragment>
			<button
				className='ExpeditionsAssignmentModal__assignment--actions--save Button--default'
				disabled={_.isEmpty(studentGroups.groups) && !studentGroups.isSelfAssignment}
				onClick={() => {
					handleAssignExpedition(studentGroups)
				}}>
				Save Assignments
			</button>
			<button
				className='ExpeditionsAssignmentModal__assignment--actions--cancel Button--cancel'
				onClick={() => handleClose()}>
				Cancel Assignments
			</button>
		</React.Fragment>
	)
}

const DeleteActionButtons = ({ handleClose }) => {
	const { isSubmitted, studentGroups } = React.useContext(ExpeditionContext)
	const { handleDeleteAssignment } = useExpeditionUtils()
	return isSubmitted ? (
		<button
			className='ExpeditionsAssignmentModal__assignment--actions--done Button--goAhead'
			onClick={() => handleClose()}>
			Done
		</button>
	) : (
		<React.Fragment>
			<button
				className='ExpeditionsAssignmentModal__assignment--actions--save Button--alert'
				onClick={() => {
					handleDeleteAssignment(studentGroups)
				}}>
				Confirm Deletion
			</button>
			<button
				className='ExpeditionsAssignmentModal__assignment--actions--cancel Button--cancel'
				onClick={() => {
					handleClose()
				}}>
				Cancel Deletion
			</button>
		</React.Fragment>
	)
}
export default ExpeditionsAssignmentModal
